/*
 * CAPTURE-OWNED MODULE
 * TemplateZoneViewer — a toolbar-less PDF host for the template zone editor.
 *
 * Renders ALL PDF pages stacked and continuously scrollable, and mounts a
 * per-page overlay subtree on EVERY page via the `renderPageOverlay` render
 * prop. Each page owns its own overlays, so OCR word boxes and drawn zones stay
 * visible on every page as you scroll — but only the ACTIVE page's subtree is
 * interactive. The parent decides what "active" vs "read-only" means; the
 * viewer just tells it which page is active via the `isActive` flag.
 *
 * The single-page coordinate model is preserved exactly: each page lives in its
 * own page-sized, position:relative `.tzv-page-wrap`, and its overlay is mounted
 * inset:0 as a sibling of <Page>. Every overlay measures against THAT wrapper,
 * so its coordinate math is identical no matter where the page has scrolled to.
 * The invariant holds: exactly ONE interactive, self-measuring overlay at a time
 * (only the active page's subtree wires up interaction).
 *
 * Overlay subtrees use a stable per-page key (the page wrapper's own position),
 * so flipping the active page never remounts a page's overlay subtree — a
 * per-page OcrWordOverlay keeps its fetched words instead of refetching on every
 * scroll.
 *
 * Active-page detection is scroll-driven (IntersectionObserver → most-visible
 * page) and pointer-driven; either way the active page is published through the
 * existing `onPageChange` contract, so the parent (TemplateEditorPage) keeps
 * filtering the interactive stack by currentPageNumber exactly as before.
 * Prev/next nav scrolls the target page into view.
 *
 * The observer is built once per document (deps: numPages only). It reads the
 * active page and setActivePage through refs; when it depended on them, every
 * page change tore it down, and the new observer's first notification put the
 * most-visible page back (a click on page 2 flipped straight back to page 1).
 *
 * Pointer-driven: a press on an inactive page activates it synchronously
 * (flushSync, so its interactive overlays mount) and the same press is then
 * replayed on whatever is now under the pointer, so one gesture both activates
 * the page and draws, selects or picks on it. The stale press never reaches
 * React; the click that follows it is redirected to the replayed target.
 *
 * While a button is held (pointerdown in any page until pointerup, pointercancel,
 * mouseup, window blur or Escape) the active page is frozen: the observer keeps
 * its ratios but publishes nothing, and the scroll container is overflow-locked
 * so a drag can never turn into a scroll.
 *
 * Edge auto-scroll (utils/dragAutoScroll): a gesture in a page overlay calls
 * beginDragScroll(press) / endDragScroll() from DragScrollContext. While it is
 * live, one requestAnimationFrame loop scrolls the container when the pointer
 * nears an edge, by writing scrollTop/scrollLeft (the overflow lock blocks the
 * wheel, not those writes), and after each frame that moved it dispatches one
 * marked mousemove on window at the last pointer position. The active page
 * stays frozen throughout. The loop stops on endDragScroll, on every release
 * of the hold, on a document change and on unmount.
 *
 * This is deliberately NOT SimplePDFViewer: no Fabric, no annotation toolbar.
 * The worker is configured once via the shared utils/pdfWorkerConfig.
 *
 * TODO(perf): no windowing — every page renders a canvas. Typical template
 * documents are 1-5 pages, so this is fine; if very long documents appear here,
 * virtualize the page list (render only pages near the viewport).
 *
 * Prop contract mirrors what TemplateEditorPage already calls with:
 *   document            : { id, downloadUrl, filename }  (fileUrl ← downloadUrl)
 *   renderPageOverlay(pageNumber, { isActive })
 *                       : returns the overlay subtree rendered inset:0 over the
 *                         given 1-based page. Called for every page; isActive is
 *                         true only for the current active page.
 *   onPageChange(n)     : 1-based page number on navigation
 *   currentPageNumber   : optional controlled 1-based page (keeps the viewer in
 *                         lockstep with the editor's page-filtered overlays; falls
 *                         back to internal state when omitted)
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Document, Page } from 'react-pdf';
import { PDF_DOCUMENT_OPTIONS } from '../utils/pdfSafety';
import '../utils/pdfWorkerConfig'; // shared, guarded worker config — do NOT add a 2nd setter
import {
  DragScrollContext, AUTOSCROLL_MOVE, PRESS_THRESHOLD_PX, edgeSpeed, frameStep, prefersReducedMotion,
} from '../utils/dragAutoScroll';
import './TemplateZoneViewer.css';

const MIN_SCALE = 0.4;
const MAX_SCALE = 3;

// Marks the events this viewer re-dispatches, so its own listeners let them pass.
const REPLAYED = '__tzvReplayed';

// Re-dispatch a pointer or mouse event of `type` on `target` with the
// coordinates and buttons of `src`. jsdom (tests) has no PointerEvent.
function replayEvent(type, src, target) {
  const pointer = type.startsWith('pointer');
  const Ctor = pointer && typeof window.PointerEvent === 'function' ? window.PointerEvent : window.MouseEvent;
  const init = {
    bubbles: true, cancelable: true, composed: true, view: window,
    clientX: src.clientX, clientY: src.clientY, screenX: src.screenX, screenY: src.screenY,
    button: src.button, buttons: src.buttons,
    ctrlKey: src.ctrlKey, shiftKey: src.shiftKey, altKey: src.altKey, metaKey: src.metaKey,
  };
  if (pointer) Object.assign(init, { pointerId: src.pointerId, pointerType: src.pointerType, isPrimary: src.isPrimary });
  const ev = new Ctor(type, init);
  if (pointer && ev.pointerId === undefined) Object.defineProperty(ev, 'pointerId', { value: src.pointerId });
  ev[REPLAYED] = true;
  return target.dispatchEvent(ev);
}

// Nearest element containing both a and b.
function commonAncestor(a, b) {
  for (let n = a; n; n = n.parentElement) if (n.contains(b)) return n;
  return null;
}

export default function TemplateZoneViewer({
  document: doc,
  renderPageOverlay,
  onPageChange,
  currentPageNumber, // optional controlled page
  onNumPagesChange, // optional: (n) => void once the document's page count is known
}) {
  const fileUrl = doc?.downloadUrl || null;
  const [numPages, setNumPages] = useState(null);
  const [internalPage, setInternalPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loadError, setLoadError] = useState(null);
  const [scrollLocked, setScrollLocked] = useState(false);

  // Controlled when the editor passes currentPageNumber; uncontrolled otherwise.
  const pageNumber = currentPageNumber != null ? currentPageNumber : internalPage;

  const scrollRef = useRef(null);
  const pageRefs = useRef([]); // 0-based index -> page wrapper element
  const ratiosRef = useRef(new Map()); // 0-based index -> intersection ratio
  // Latest active page, read inside the IO callback without re-subscribing.
  // Assigned during render (not in an effect) so a press handled right after a
  // flushSync activation already sees the new page.
  const pageNumberRef = useRef(pageNumber);
  pageNumberRef.current = pageNumber;
  // True while a button is held in the pages: the active page is frozen.
  const heldRef = useRef(false);

  // Publish the active page WITHOUT scrolling (scroll-/click-driven changes).
  const setActivePage = useCallback((n) => {
    const clamped = Math.max(1, Math.min(numPages || 1, n));
    if (currentPageNumber == null) setInternalPage(clamped);
    if (clamped !== pageNumberRef.current && onPageChange) onPageChange(clamped);
  }, [numPages, currentPageNumber, onPageChange]);
  // The observer and the pointer listeners call the latest setActivePage
  // through this ref, so neither depends on the page state.
  const setActivePageRef = useRef(setActivePage);
  setActivePageRef.current = setActivePage;

  // Nav buttons: set active AND scroll the target page into view.
  const navigateToPage = useCallback((n) => {
    const clamped = Math.max(1, Math.min(numPages || 1, n));
    setActivePage(clamped);
    const el = pageRefs.current[clamped - 1];
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [numPages, setActivePage]);

  const onDocLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
    if (onNumPagesChange) onNumPagesChange(n);
    setLoadError(null);
    pageRefs.current = new Array(n).fill(null);
    ratiosRef.current = new Map();
    // Clamp the active page if the new document has fewer pages.
    const clamped = Math.max(1, Math.min(n, pageNumberRef.current));
    if (currentPageNumber == null) {
      setInternalPage(clamped);
    } else if (clamped !== pageNumberRef.current && onPageChange) {
      onPageChange(clamped);
    }
  }, [currentPageNumber, onPageChange, onNumPagesChange]);

  // Scroll-driven active page: the most-visible page wins. Observing against the
  // scroll container as root keeps it robust to zoom and page height changes.
  useEffect(() => {
    if (!numPages || !scrollRef.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const idx = Number(entry.target.dataset.pageIndex);
        ratiosRef.current.set(idx, entry.isIntersecting ? entry.intersectionRatio : 0);
      }
      let bestIdx = -1;
      let bestRatio = -1;
      for (const [idx, ratio] of ratiosRef.current.entries()) {
        if (ratio > bestRatio) { bestRatio = ratio; bestIdx = idx; }
      }
      // Frozen while a button is held: a drag never changes the page under it.
      if (heldRef.current) return;
      if (bestIdx >= 0 && bestRatio > 0) setActivePageRef.current(bestIdx + 1);
    }, { root: scrollRef.current, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });

    pageRefs.current.forEach((el) => { if (el) io.observe(el); });
    return () => io.disconnect();
  }, [numPages]);

  // ---- Edge auto-scroll ----
  // dragScrollRef: { raf, origin, last, armed, prevTs, reduced, onMove } while live.
  const dragScrollRef = useRef(null);

  const endDragScroll = useCallback(() => {
    const d = dragScrollRef.current;
    if (!d) return;
    dragScrollRef.current = null;
    cancelAnimationFrame(d.raf);
    window.removeEventListener('pointermove', d.onMove, true);
    window.removeEventListener('mousemove', d.onMove, true);
  }, []);

  // press: the client point the gesture started at. current: where the pointer
  // is now, when the gesture starts after it has already moved.
  const beginDragScroll = useCallback((press, current = press) => {
    if (dragScrollRef.current || !press) return;
    const d = {
      raf: 0,
      origin: { x: press.clientX, y: press.clientY },
      last: { x: current.clientX, y: current.clientY },
      armed: false,
      prevTs: null,
      reduced: prefersReducedMotion(),
    };
    const arm = () => {
      if (!d.armed && Math.hypot(d.last.x - d.origin.x, d.last.y - d.origin.y) > PRESS_THRESHOLD_PX) d.armed = true;
    };
    d.onMove = (e) => {
      if (e[AUTOSCROLL_MOVE]) return;
      d.last = { x: e.clientX, y: e.clientY };
      arm();
    };
    arm();
    const tick = (ts) => {
      if (dragScrollRef.current !== d) return;
      d.raf = requestAnimationFrame(tick);
      const dt = d.prevTs == null ? 1000 / 60 : ts - d.prevTs;
      d.prevTs = ts;
      const scroller = scrollRef.current;
      if (!d.armed || !scroller) return;
      // Measured every frame: the pane can move or resize mid-drag. Edges are
      // the part of the container inside the window.
      const r = scroller.getBoundingClientRect();
      const top = Math.max(r.top, 0);
      const bottom = Math.min(r.bottom, window.innerHeight || r.bottom);
      const left = Math.max(r.left, 0);
      const right = Math.min(r.right, window.innerWidth || r.right);
      const dy = frameStep(edgeSpeed(d.last.y, top, bottom, d.reduced), dt);
      const dx = frameStep(edgeSpeed(d.last.x, left, right, d.reduced), dt);
      if (!dx && !dy) return;
      const { scrollTop, scrollLeft } = scroller;
      if (dy) scroller.scrollTop = scrollTop + dy;
      if (dx) scroller.scrollLeft = scrollLeft + dx;
      if (scroller.scrollTop === scrollTop && scroller.scrollLeft === scrollLeft) return;
      const ev = new MouseEvent('mousemove', {
        bubbles: true, cancelable: true, view: window,
        clientX: d.last.x, clientY: d.last.y, button: 0, buttons: 1,
      });
      ev[AUTOSCROLL_MOVE] = true;
      window.dispatchEvent(ev);
    };
    window.addEventListener('pointermove', d.onMove, true);
    window.addEventListener('mousemove', d.onMove, true);
    dragScrollRef.current = d;
    d.raf = requestAnimationFrame(tick);
  }, []);

  const dragScroll = useMemo(() => ({ beginDragScroll, endDragScroll }), [beginDragScroll, endDragScroll]);

  // A new document, or unmount, ends any loop.
  useEffect(() => endDragScroll, [numPages, endDragScroll]);

  // Release the hold (freeze + scroll lock + auto-scroll) when the button comes
  // up anywhere, the press is cancelled, the window loses focus, or Escape is
  // pressed.
  useEffect(() => {
    const release = () => {
      heldRef.current = false;
      setScrollLocked(false);
      endDragScroll();
    };
    const onKey = (e) => { if (e.key === 'Escape') release(); };
    // Only the window's own blur: an element losing focus (the press itself
    // blurs a focused input) is not a release.
    const onBlur = (e) => { if (e.target === window) release(); };
    const types = ['pointerup', 'pointercancel', 'mouseup'];
    types.forEach((t) => window.addEventListener(t, release, true));
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    return () => {
      types.forEach((t) => window.removeEventListener(t, release, true));
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [endDragScroll]);

  // A press in a page holds it; a press on an INACTIVE page also activates it
  // and is replayed on the newly interactive layer under the pointer. Native
  // capture listeners on the scroll container: they run before React's bubble
  // dispatch at the root, so the stale press is stopped before any React
  // handler sees it.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!numPages || !scroller) return undefined;
    // replay: { target, wrap } for the press replayed in this gesture.
    let replay = null;
    let swallowMouseDown = false;

    const onPointerDown = (e) => {
      if (e[REPLAYED]) return;
      replay = null;
      swallowMouseDown = false;
      const wrap = e.target.closest?.('[data-page-index]');
      if (!wrap || !scroller.contains(wrap) || e.button !== 0) return;
      heldRef.current = true;
      setScrollLocked(true);
      const pageNo = Number(wrap.dataset.pageIndex) + 1;
      if (pageNo === pageNumberRef.current) return;

      e.stopPropagation();
      flushSync(() => setActivePageRef.current(pageNo));
      // A controlled parent that refused the change leaves the page inactive.
      if (pageNumberRef.current !== pageNo) return;
      const hit = window.document.elementFromPoint?.(e.clientX, e.clientY);
      const target = hit && wrap.contains(hit) ? hit : null;
      if (!target) return;
      replay = { target, wrap };
      swallowMouseDown = true;
      replayEvent('pointerdown', e, target);
      replayEvent('mousedown', e, target);
    };

    // The browser's own mousedown for the stale press (its target is the
    // read-only layer that was under the pointer) is not delivered.
    const onMouseDown = (e) => {
      if (e[REPLAYED] || !swallowMouseDown) return;
      swallowMouseDown = false;
      e.stopPropagation();
      e.preventDefault();
    };

    // The click that ends the gesture targets the ancestor shared by the stale
    // press and the release; deliver it where a click would have gone had the
    // page been active at the press.
    const onClick = (e) => {
      if (e[REPLAYED] || !replay) return;
      const { target, wrap } = replay;
      replay = null;
      if (!target.isConnected) return;
      const up = window.document.elementFromPoint?.(e.clientX, e.clientY) || e.target;
      const to = commonAncestor(target, up);
      if (!to || to === e.target || !wrap.contains(to)) return;
      e.stopPropagation();
      e.preventDefault();
      replayEvent('click', e, to);
    };

    scroller.addEventListener('pointerdown', onPointerDown, true);
    scroller.addEventListener('mousedown', onMouseDown, true);
    scroller.addEventListener('click', onClick, true);
    return () => {
      scroller.removeEventListener('pointerdown', onPointerDown, true);
      scroller.removeEventListener('mousedown', onMouseDown, true);
      scroller.removeEventListener('click', onClick, true);
    };
  }, [numPages]);

  if (!fileUrl) {
    return <div className="tzv-empty">No document URL.</div>;
  }

  return (
    <div className="tzv-root">
      {/* Toolbar-less nav: page + display-only zoom (zoom never alters stored coords) */}
      <div className="tzv-nav">
        <button onClick={() => navigateToPage(pageNumber - 1)} disabled={pageNumber <= 1} title="Previous page">‹</button>
        <span className="tzv-page-ind">{pageNumber} / {numPages || '—'}</span>
        <button onClick={() => navigateToPage(pageNumber + 1)} disabled={!numPages || pageNumber >= numPages} title="Next page">›</button>
        <span className="tzv-sep" />
        <button onClick={() => setScale(s => Math.max(MIN_SCALE, +(s - 0.1).toFixed(2)))} title="Zoom out">−</button>
        <span className="tzv-zoom-ind">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(MAX_SCALE, +(s + 0.1).toFixed(2)))} title="Zoom in">+</button>
      </div>

      {/* overflow-locked during a zone drag so the drag never becomes a scroll */}
      <div
        className="tzv-scroll"
        ref={scrollRef}
        style={scrollLocked ? { overflowY: 'hidden' } : undefined}
      >
        <DragScrollContext.Provider value={dragScroll}>
          <Document
            file={fileUrl}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={onDocLoad}
            onLoadError={(e) => setLoadError(e?.message || 'Failed to load PDF')}
            loading={<div className="tzv-loading">Loading document…</div>}
            error={<div className="tzv-error">Could not load document preview.</div>}
          >
            {/* One page-sized, position:relative wrapper per page. EVERY wrapper
                renders its own overlay subtree (via renderPageOverlay) inset:0 as
                a sibling of <Page>, so overlays measure against the page box
                exactly as in the single-page model. Only the active page's subtree
                is interactive; inactive pages carry read-only display layers. The
                per-page `key` is stable across active-page changes so a page's
                overlay subtree (and its per-page OCR fetch) is never remounted. */}
            {numPages ? Array.from({ length: numPages }, (_, i) => {
              const isActive = (i + 1) === pageNumber;
              return (
                <div
                  key={i}
                  data-page-index={i}
                  ref={(el) => { pageRefs.current[i] = el; }}
                  className={`tzv-page-wrap${isActive ? ' tzv-page-wrap--active' : ''}`}
                  // A press on any page holds it and, when inactive, activates
                  // it (the native pointerdown listener above).
                >
                  <Page
                    pageNumber={i + 1}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={<div className="tzv-loading">Rendering page…</div>}
                  />
                  {renderPageOverlay && renderPageOverlay(i + 1, { isActive })}
                </div>
              );
            }) : null}
          </Document>
        </DragScrollContext.Provider>
        {loadError && <div className="tzv-error">PDF error: {loadError}</div>}
      </div>
    </div>
  );
}
